<template>
  <div class="video-player-container">
    <div class="page-header">
      <el-button type="text" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
      <span class="title">{{ courseInfo.courseName }}</span>
    </div>

    <div class="player-layout">
      <div class="player-main">
        <div class="player-area">
          <video
            ref="nativePlayer"
            class="native-player"
            controls
            playsinline
            :src="currentSrc"
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="isPlaying = false"
          ></video>
          <div v-if="!isPlaying" class="player-cover" @click="handleBigPlay">
            <div class="big-play-btn">
              <i class="el-icon-video-play"></i>
            </div>
            <span class="play-tip">点击播放</span>
          </div>
        </div>
        <div class="player-info">
          <div class="info-row">
            <el-tag size="mini" type="info">{{ courseInfo.courseType }}</el-tag>
            <span class="instructor">
              <i class="el-icon-user"></i>
              讲师：{{ courseInfo.instructor || '--' }}
            </span>
            <span class="time">
              <i class="el-icon-time"></i>
              {{ courseInfo.createTime || '--' }}
            </span>
          </div>
          <div v-if="currentChapter" class="current-title">
            正在播放：<strong>{{ currentChapter.title }}</strong>
          </div>
          <div v-if="courseInfo.description" class="course-desc">
            {{ courseInfo.description }}
          </div>
        </div>
      </div>

      <div class="player-side">
        <div class="side-title">
          <i class="el-icon-notebook-2"></i>
          章节目录 <span class="count">(共 {{ chapterList.length }} 节)</span>
        </div>
        <div v-loading="loading" class="chapter-scroll">
          <div
            v-for="(item, index) in chapterList"
            :key="item.id"
            class="chapter-item"
            :class="{ active: currentChapter && currentChapter.id === item.id }"
            @click="handlePlay(item)"
          >
            <div class="chapter-index">{{ String(index + 1).padStart(2, '0') }}</div>
            <div class="chapter-thumb">
              <template v-if="item.videoUrl">
                <i :class="currentChapter && currentChapter.id === item.id ? 'el-icon-pause' : 'el-icon-video-play'"></i>
              </template>
              <span v-else class="thumb-empty">未上传视频</span>
            </div>
            <div class="chapter-info">
              <div class="chapter-title" :title="item.title">{{ item.title }}</div>
              <div class="chapter-sub" v-if="item.description">{{ item.description }}</div>
            </div>
            <div v-if="item.duration && item.duration !== '--'" class="chapter-duration">{{ item.duration }}</div>
          </div>
          <el-empty v-if="!loading && chapterList.length === 0" description="暂无章节" :image-size="60" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getChapterList } from '@/api/video/chapter'

export default {
  name: 'VideoPlayer',
  data() {
    return {
      currentSrc: '',
      isPlaying: false,
      loading: false,
      courseId: null,
      courseInfo: {
        courseName: '',
        courseType: '',
        instructor: '',
        createTime: '',
        description: '',
      },
      chapterList: [],
      currentChapter: null,
    }
  },
  created() {
    const {
      courseId,
      courseName,
      courseType,
      instructor,
      createTime,
      description,
    } = this.$route.query
    this.courseId = courseId
    this.courseInfo = {
      courseName: courseName || '',
      courseType: courseType || '',
      instructor: instructor || '',
      createTime: createTime || '',
      description: description || '',
    }
    if (courseId) {
      this.loadChapters()
    } else {
      this.$message.error('缺少课程信息，无法播放')
    }
  },
  beforeDestroy() {
    this.pauseVideo()
  },
  methods: {
    goBack() {
      this.$router.back()
    },
    resolveCoverUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    pauseVideo() {
      const video = this.$refs.nativePlayer
      if (video) video.pause()
    },
    async loadChapters() {
      this.loading = true
      try {
        const res = await getChapterList(Number(this.courseId))
        if (res && (res.code === 200 || res.success)) {
          // 新接口返回课程详情对象，里面嵌套章节数组
          const data = res.data || {}
          const list = Array.isArray(data)
            ? data
            : (data.chapters || data.chapterList || data.list || [])
          this.chapterList = list.map(item => {
            const video = item.video || {}
            const materials = item.materials || []
            return {
              id: item.id,
              title: item.sectionName || '',
              description: video.videoName || '',
              duration: video.duration ? this.formatDuration(video.duration) : '--',
              videoUrl: video.videoUrl || '',
              videoName: video.videoName || '',
              materials: materials.map(m => ({
                id: m.id,
                name: m.materialName || '',
                fileName: m.materialName || '',
                fileUrl: m.fileUrl || '',
                url: m.fileUrl || '',
                previewUrl: m.fileUrl || '',
                fileType: m.fileType || '',
              })),
              chapterNo: item.chapterNo,
              sectionNo: item.sectionNo,
            }
          })
          if (this.chapterList.length > 0) {
            // 只默认选中第一章，不自动播放
            this.currentChapter = this.chapterList[0]
          }
        }
      } catch (e) {
        console.error('加载章节目录失败', e)
        this.$message.error('加载章节目录失败')
      } finally {
        this.loading = false
      }
    },
    formatDuration(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    handlePlay(chapter) {
      if (!chapter || !chapter.videoUrl) {
        this.$message.warning('该章节暂无可播放视频')
        return
      }
      this.currentChapter = chapter
      // 切换视频时只切换源，不自动播放
      this.currentSrc = chapter.videoUrl
      this.isPlaying = false
    },
    handleBigPlay() {
      if (this.chapterList.length === 0) {
        this.$message.warning('暂无章节可播放')
        return
      }
      const target = this.currentChapter && this.currentChapter.videoUrl
        ? this.currentChapter
        : this.chapterList.find(c => c.videoUrl) || this.chapterList[0]
      if (!target.videoUrl) {
        this.$message.warning('暂无视频可播放')
        return
      }
      // 同一章节已加载过：直接播放
      if (this.currentChapter && this.currentChapter.id === target.id && this.currentSrc) {
        this.$nextTick(() => {
          const video = this.$refs.nativePlayer
          if (video && video.paused) {
            const p = video.play()
            if (p && typeof p.catch === 'function') {
              p.catch(() => { /* 自动播放被阻止时忽略 */ })
            }
          }
        })
        return
      }
      // 切换章节：更新源后播放
      this.currentChapter = target
      this.currentSrc = target.videoUrl
      this.$nextTick(() => {
        const video = this.$refs.nativePlayer
        if (!video) return
        // 等元数据加载完再播放，避免空 src 调用报错
        const tryPlay = () => {
          const p = video.play()
          if (p && typeof p.catch === 'function') {
            p.catch(() => { /* 浏览器自动播放策略限制时忽略 */ })
          }
        }
        if (video.readyState >= 2) {
          tryPlay()
        } else {
          video.addEventListener('loadeddata', tryPlay, { once: true })
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.video-player-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  background: #f0f2f5;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;

  .title {
    margin-left: 12px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
}

.player-layout {
  flex: 1;
  display: flex;
  height: 100%;
  padding: 16px;
  gap: 16px;
  box-sizing: border-box;
  overflow: auto;
}

.player-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.player-area {
  width: 100%;
  height: 0px;
  padding-bottom: 56.25%; /* 16:9 比例 */
  position: relative;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;

  .native-player {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }

  .player-cover {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    z-index: 2;

    .big-play-btn {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s, background 0.2s;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);

      i {
        font-size: 40px;
        color: #fff;
        line-height: 1;
      }
    }

    .play-tip {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
      letter-spacing: 1px;
    }

    &:hover .big-play-btn {
      transform: scale(1.08);
      background: rgba(255, 255, 255, 0.42);
    }
  }
}

.player-info {
  margin-top: 16px;
  background: #fff;
  border-radius: 6px;
  padding: 16px 20px;

  .info-row {
    display: flex;
    align-items: center;
    gap: 16px;
    color: #606266;
    font-size: 13px;

    .instructor,
    .time {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .current-title {
    margin-top: 10px;
    font-size: 14px;
    color: #303133;

    strong {
      color: #409eff;
    }
  }

  .course-desc {
    margin-top: 8px;
    color: #606266;
    font-size: 13px;
    line-height: 1.6;
    word-break: break-all;
    white-space: pre-wrap;
  }
}

.player-side {
  width: 360px;
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;

  .side-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 10px;
    margin-bottom: 10px;

    .count {
      font-size: 12px;
      font-weight: normal;
      color: #909399;
      margin-left: 4px;
    }
  }

  .chapter-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .chapter-item {
    display: flex;
    align-items: center;
    padding: 10px 8px;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 6px;
    transition: background 0.2s;

    &:hover {
      background: #f5f7fa;
    }

    &.active {
      background: #ecf5ff;

      .chapter-index {
        background: #409eff;
        color: #fff;
      }

      .chapter-thumb {
        color: #409eff;
      }

      .chapter-title {
        color: #409eff;
      }
    }

    .chapter-index {
      width: 24px;
      height: 24px;
      line-height: 24px;
      text-align: center;
      font-size: 12px;
      color: #909399;
      background: #f0f2f5;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .chapter-thumb {
      width: 60px;
      height: 36px;
      background: #f0f2f5;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 10px;
      color: #909399;
      flex-shrink: 0;

      .thumb-empty {
        font-size: 10px;
        color: #c0c4cc;
        text-align: center;
        line-height: 1.2;
        padding: 0 2px;
      }
    }

    .chapter-info {
      flex: 1;
      min-width: 0;

      .chapter-title {
        font-size: 13px;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chapter-sub {
        margin-top: 2px;
        font-size: 11px;
        color: #909399;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .chapter-duration {
      font-size: 12px;
      color: #909399;
      flex-shrink: 0;
      margin-left: 8px;
    }
  }
}
</style>
